import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Button, Typography, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PsychologyIcon from "@mui/icons-material/Psychology";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ContactDialog from "./ContactDialog";

import { HEALTH_STATUS_COLORS, SP_VISIT_COLORS, getSpVisitAge } from "../../constants/clinicalDashboard";
import TableSearchBar from "./TableSearchBar";
import { useObservationsModal } from "../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../components/Advanced/DataGrid/observationsColumn";

const PPV_OPTIONS = ["All", "Low", "Medium", "High"];
const STATUS_OPTIONS = ["All", "Pending", "Contacted", "In Progress", "Resolved", "Removed"];

/**
 * Mapa de colores para PPV-ER risk level.
 */
const PPV_COLORS = {
  LOW:    { text: "#16A34A", bg: "#DCFCE7" },
  MEDIUM: { text: "#D97706", bg: "#FEF3C7" },
  HIGH:   { text: "#DC2626", bg: "#FEE2E2" },
};

const CONTACT_STATUS_COLORS = {
  "Pending":     { bg: "#FEF3C7", text: "#92400E" },
  "Contacted":   { bg: "#DBEAFE", text: "#1D4ED8" },
  "In Progress": { bg: "#E0E7FF", text: "#4338CA" },
  "Resolved":    { bg: "#DCFCE7", text: "#166534" },
  "Removed":     { bg: "#F1F5F9", text: "#64748B" },
};

const getPpvChip = (value) => {
  if (value == null) {
    return { label: "N/A", text: "#94A3B8", bg: "#F1F5F9" };
  }
  const upper = String(value).toUpperCase();
  const match = PPV_COLORS[upper];
  if (!match) return { label: value, text: "#94A3B8", bg: "#F1F5F9" };
  return { label: upper, ...match };
};

/**
 * Tabla de top ER Frequent Flyers con contacto, careplan, y acciones.
 *
 * @param {{ flyers: Array, onCreateTask: (row) => void }} props
 */
export default function ERTable({ flyers = [], onCreateTask }) {
  const navigate = useNavigate();
  const { showObservationsModal } = useObservationsModal();
  const [search, setSearch] = useState("");
  const [ppvFilter, setPpvFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [monthFilter, setMonthFilter] = useState("All");
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactPatient, setContactPatient] = useState(null);

  const handleOpenContact = (row) => {
    setContactPatient(row);
    setContactDialogOpen(true);
  };

  // Collect unique months for dropdown
  const monthOptions = useMemo(() => {
    const unique = [...new Set(flyers.map((r) => r.erMonth).filter(Boolean))];
    return ["All", ...unique.sort().reverse()];
  }, [flyers]);

  const filteredRows = useMemo(() => {
    const lowerSearch = search.toLowerCase();
    return flyers.filter((row) => {
      if (lowerSearch && !row.name?.toLowerCase().includes(lowerSearch)) return false;
      if (ppvFilter !== "All" && String(row.ppvER).toUpperCase() !== ppvFilter.toUpperCase()) return false;
      if (statusFilter !== "All" && row.contactStatus !== statusFilter) return false;
      if (monthFilter !== "All" && row.erMonth !== monthFilter) return false;
      return true;
    });
  }, [flyers, search, ppvFilter, statusFilter, monthFilter]);

  const columns = [
    {
      field: "name",
      headerName: "Patient",
      flex: 1,
      minWidth: 150,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} noWrap>
          {value}
        </Typography>
      ),
    },
    {
      field: "erMonth",
      headerName: "Month",
      width: 85,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        if (!value) return "—";
        const [y, m] = value.split("-");
        const label = new Date(y, m - 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
        return (
          <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: "#EDE9FE", color: "#6D28D9" }} />
        );
      },
    },
    {
      field: "erEvents",
      headerName: "ER Events",
      width: 85,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip
          label={value}
          size="small"
          sx={{
            fontWeight: 700,
            bgcolor: "#DC2626",
            color: "#FFFFFF",
            minWidth: 36,
          }}
        />
      ),
    },
    {
      field: "preventableER",
      headerName: "Prev.",
      width: 65,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ppvER",
      headerName: "PPV-ER",
      width: 85,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const { label, text, bg } = getPpvChip(value);
        return (
          <Chip
            label={label}
            size="small"
            sx={{ fontWeight: 600, color: text, bgcolor: bg, minWidth: 50 }}
          />
        );
      },
    },
    {
      field: "healthStatus",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const palette = HEALTH_STATUS_COLORS[value] || HEALTH_STATUS_COLORS["No Status"];
        return (
          <Chip
            label={value || "N/A"}
            size="small"
            sx={{
              fontWeight: 600,
              color: palette.text,
              bgcolor: palette.bg,
              border: `1px solid ${palette.border}`,
            }}
          />
        );
      },
    },
    {
      field: "hasCarePlan",
      headerName: "Care Plan",
      width: 80,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <CheckCircleIcon sx={{ fontSize: 18, color: "#16A34A" }} />
        ) : (
          <CancelIcon sx={{ fontSize: 18, color: "#D1D5DB" }} />
        ),
    },
    {
      field: "contactStatus",
      headerName: "Contact Status",
      width: 115,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const colors = CONTACT_STATUS_COLORS[value] || { bg: "#F1F5F9", text: "#64748B" };
        return (
          <Chip
            label={value || "—"}
            size="small"
            sx={{ fontWeight: 600, fontSize: "0.68rem", bgcolor: colors.bg, color: colors.text, height: 22 }}
          />
        );
      },
    },
    {
      field: "timesContacted",
      headerName: "Calls",
      width: 65,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value, row }) => {
        const val = value || 0;
        if (val === 0) return <Typography variant="body2" color="text.secondary">0</Typography>;
        return (
          <Tooltip title={row.contactedBy ? `Last by: ${row.contactedBy}` : ""}>
            <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />
          </Tooltip>
        );
      },
    },
    {
      field: "behavioralHealth",
      headerName: "BH",
      width: 50,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) =>
        value ? (
          <PsychologyIcon sx={{ color: "#7C3AED", fontSize: 20 }} />
        ) : null,
    },
    {
      field: "lastSantePlusVisit",
      headerName: "SP Visit",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const age = getSpVisitAge(value);
        const colors = SP_VISIT_COLORS[age];
        if (!value) {
          return <Chip label="—" size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
        }
        const d = new Date(value);
        const label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return <Chip label={label} size="small" sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: colors.bg, color: colors.text }} />;
      },
    },
    {
      field: "observationsCount",
      headerName: "Obs",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#DBEAFE", color: "#2563EB" }} />;
      },
    },
    {
      field: "activeTasks",
      headerName: "Tasks",
      width: 55,
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const val = value || 0;
        if (val === 0) return <Box component="span" sx={{ color: "text.secondary" }}>0</Box>;
        return <Chip label={val} size="small" sx={{ height: 20, minWidth: 24, fontSize: "0.7rem", fontWeight: 700, bgcolor: "#FEF3C7", color: "#D97706" }} />;
      },
    },
    getObservationsColumn(showObservationsModal, 3),
    {
      field: "viewPatient",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Tooltip title="View Patient">
          <IconButton size="small" onClick={() => navigate(`/patient-info/${row.cipId}`)} sx={{ color: "#6366F1" }}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "contactActions",
      headerName: "Contact",
      width: 95,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContactPhoneIcon sx={{ fontSize: 16 }} />}
          onClick={() => handleOpenContact(row)}
          sx={{ textTransform: "none", fontSize: "0.72rem" }}
        >
          Contact
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 145,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AssignmentTurnedInIcon sx={{ fontSize: 16 }} />}
          onClick={() => onCreateTask?.(row)}
          sx={{ textTransform: "none", fontSize: "0.72rem" }}
        >
          Intervention
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <TableSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search patient..."
        filters={[
          { key: "ppvER", label: "PPV-ER", options: PPV_OPTIONS, value: ppvFilter },
          { key: "contactStatus", label: "Contact Status", options: STATUS_OPTIONS, value: statusFilter },
          { key: "erMonth", label: "Month", options: monthOptions, value: monthFilter },
        ]}
        onFilterChange={(key, value) => {
          if (key === "ppvER") setPpvFilter(value);
          if (key === "contactStatus") setStatusFilter(value);
          if (key === "erMonth") setMonthFilter(value);
        }}
      />

      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.cipId}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[5, 10, 25]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          borderRadius: 2,
          border: "none",
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.78rem",
          },
        }}
      />

      <ContactDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        patient={contactPatient}
      />
    </Box>
  );
}
